/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { Link } from '@fluentui/react';
import { ActionButton, IconButton } from '@fluentui/react/lib/Button';
import { Icon } from '@fluentui/react/lib/Icon';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import HTMLParser from 'html-react-parser';
import { parse } from 'marked';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { DragAndDropList } from 'src/core/components/DragAndDropList';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableProps } from 'src/core/components/TableProps';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import 'src/qnaMaker/appShell/qnaMakerEditPage/ckEditorStyles.css';
import { QnaMakerFollowUpPromptModal } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerFollowUpPromptModal';
import { useQnaMetricActions } from 'src/qnaMaker/core/hooks/useQnaMetricActions';
import { QnaMakerQnaSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { QnaMakerQnaPrompt, QnaMakerQnaStore } from 'src/qnaMaker/core/stores/QnaMakerQnaStore';

export type QnaMakerAnswerColumnRendererProps = TableProps<QnaMakerQnaStore> & {
    pairId: number;
    answer: string;
    disabled?: boolean;
    selectedQnaId: number;
    isInEditMode?: boolean;
    isContextOnly: boolean;
    isRichTextEnabled: boolean;
    isAddActionDisabled: boolean;
    isPromptModalHidden: boolean;
    prompts: QnaMakerQnaPrompt[];
    qnaPairsJson: QnaMakerQnaSwagger[];
    setIsPromptModalHidden: (value: boolean) => void;
    onChange: (ans: string, prompts: QnaMakerQnaPrompt[], modifiedPair?: Partial<QnaMakerQnaSwagger>) => void;
};

const messages = defineMessages({
    edit: {
        id: 'qnaMaker.qnaMakerAnswerColumnRenderer.edit',
        defaultMessage: 'Edit'
    },
    add: {
        id: 'qnaMaker.qnaMakerAnswerColumnRenderer.add',
        defaultMessage: 'Add follow up prompts'
    },
    textPlaceholder: {
        id: 'qnaMaker.qnaMakerAnswerColumnRenderer.textPlaceholder',
        defaultMessage: 'Add answer and press enter'
    },
    deletePrompt: {
        id: 'qnaMaker.qnaMakerAnswerColumnRenderer.deletePrompt',
        defaultMessage: 'Delete prompt'
    },
    seeMore: {
        id: 'qnaMaker.qnaMakerAnswerColumnRenderer.seeMore',
        defaultMessage: 'see more'
    },
    seeLess: {
        id: 'qnaMaker.qnaMakerAnswerColumnRenderer.seeLess',
        defaultMessage: 'see less'
    }
});

const { styleComponent } = getStylistV2('QnaMakerAnswerColumnRenderer');
const localTheme = getScopedTheme('QnaMakerAnswerColumnRenderer');

const PromptItem = styleComponent<IStackProps>(Stack)(
    'PromptItem',
    (_, { border, color }) => ({
        color,
        border,
        boxSizing: 'border-box',
        borderRadius: '5px',
        height: '32px',
        maxWidth: '250px',
        backgroundColor: localTheme.promptItemBackgroundColor
    }),
    {
        cssVars: { border: '', color: '' },
        cssVarsSetter: ({ isDisabled }: { isDisabled?: boolean }) => ({
            border: isDisabled ? `1px solid ${localTheme.promptItemDisabledColor}` : '1px solid',
            color: isDisabled ? localTheme.promptItemDisabledColor : undefined
        })
    }
);

/**
 * Applying some modification on the ckEditor output to match the customized version requirements.
 */
const handleTags = (data: string) => {
    data = data.split('<figure class="image">').join('');
    data = data.split('</figure>').join('');

    data = data.replace(/</g, '&lt;');
    data = data.replace(/>/g, '&gt;');

    return data;
};

export const markdownTextToHtmlElements = (text: string) => {
    const html = parse(handleTags(text));

    return HTMLParser(html);
};

const COLLAPSED_ANSWER_CHAR_LIMIT = 1500;

/**
 * A Renderer for the answer column in QnA Maker question answer pairs table.
 */
export const QnaMakerAnswerColumnRenderer = observer((props: QnaMakerAnswerColumnRendererProps) => {
    const {
        answer,
        pairId,
        prompts,
        onChange,
        qnaPairsJson,
        selectedQnaId,
        isContextOnly,
        disabled = false,
        isRichTextEnabled,
        isPromptModalHidden,
        isAddActionDisabled,
        isInEditMode = false,
        setIsPromptModalHidden
    } = props;

    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { serviceName } = getPortalFromUrl();
    const [editableAnswer, setEditableAnswer] = React.useState(answer);
    const [isReadOnlyAnswerCollapsed, serIsReadOnlyAnswerCollapsed] = React.useState(true);
    const [promptToBeEditedId, setPromptToBeEditedId] = React.useState(null);

    const { setTimeTakenToEditAnswers, increaseAnswerClicksForPair } = useQnaMetricActions();
    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);
    const { language, languageDirection, id: currentProjectId } = rootStore.currentQnaMakerProjectStore;

    // The next cases won't be valid to be included in the follow up prompt:
    //  1. Adding follow up prompt points to the same parent qna.
    //  2. Adding prompt points to a pair already linked with other prompt.
    //  3. Adding prompt that link to new qna pair isn't saved already in the backend.
    const hiddenQnaPairIds = [...prompts.map(p => p.qnaId), selectedQnaId, ...qnaPairsJson.filter(p => p.internalRef).map(p => p.id)];

    React.useEffect(() => {
        setEditableAnswer(answer);
    }, [answer]);

    const handleRichEditorOnChange = (_: any, editor: any) => {
        const data = editor.getData();
        setEditableAnswer(data);
        onChange(data, prompts);
    };

    const handleFollowUpPromptSubmit = ({ prompt, linkedPair }: { prompt: QnaMakerQnaPrompt; linkedPair: Partial<QnaMakerQnaSwagger> }) => {
        setIsPromptModalHidden(true);

        if (promptToBeEditedId) {
            setPromptToBeEditedId(null);
            onChange(
                answer,
                prompts.map(p => (p.qnaId === promptToBeEditedId ? prompt : p)),
                linkedPair
            );

            return;
        }

        const newDisplayedOrder = Math.max(...prompts.map(p => p.displayOrder)) + 1;
        onChange(answer, [...prompts, { ...prompt, displayOrder: newDisplayedOrder }], linkedPair);
    };

    const onSubmitAnswer = () => onChange(editableAnswer, prompts);

    const handleEditAnswer = (newAnswer: string) => {
        setEditableAnswer(newAnswer);
        onChange(newAnswer, prompts);
    };

    const handleDeletePromptInEditMode = (_prompt: QnaMakerQnaPrompt) => {
        increaseAnswerClicksForPair(pairId);
        onChange(
            editableAnswer,
            prompts.filter(p => p.displayText !== _prompt.displayText)
        );
    };

    const onRenderPromptItem = (_prompt: QnaMakerQnaPrompt) => (
        <Stack key={`${_prompt.qnaId}-${_prompt.displayText}`} horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
            <PromptItem
                horizontal
                verticalFill
                key={_prompt.qnaId}
                verticalAlign="center"
                customProps={{ isDisabled: false }}
                styles={{ root: { cursor: 'pointer' } }}
                tokens={{ padding: '8px', childrenGap: 5 }}>
                <RBACTooltip
                    hidden={canWrite}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="dragButtonLanguageToolTip"
                    componentSurroundedByToolTip={<Icon iconName={'DragObject'} />}
                />

                <RBACTooltip
                    hidden={canWrite}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="addFollowUpButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <TruncatedText
                            block
                            nowrap
                            aria-haspopup
                            maxWidth={150}
                            dir={languageDirection}
                            title={_prompt.displayText}
                            aria-label={_prompt.displayText}
                            styles={{ root: { color: localTheme.textColor } }}
                            onClick={() => {
                                if (canWrite) {
                                    setPromptToBeEditedId(_prompt.qnaId);
                                    setIsPromptModalHidden(false);
                                }
                            }}>
                            {_prompt.displayText}
                        </TruncatedText>
                    }
                />
            </PromptItem>
            {isInEditMode && (
                <IconButton
                    aria-label={localize(messages.deletePrompt)}
                    iconProps={{ iconName: 'Delete', styles: { root: { fontSize: '14px' } } }}
                    onClick={() => {
                        handleDeletePromptInEditMode(_prompt);
                    }}
                />
            )}
        </Stack>
    );

    const getReadOnlyAnswer = () => {
        const showCollapsibleControl = answer.length > COLLAPSED_ANSWER_CHAR_LIMIT;
        const displayedAnswer =
            showCollapsibleControl && isReadOnlyAnswerCollapsed ? `${answer.slice(0, COLLAPSED_ANSWER_CHAR_LIMIT)}...` : answer;

        const getCollapsibleControlComponent = () => (
            <>
                &nbsp;
                <Link onClick={() => serIsReadOnlyAnswerCollapsed(c => !c)}>
                    {localize(isReadOnlyAnswerCollapsed ? messages.seeMore : messages.seeLess)}
                </Link>
            </>
        );

        return (
            <Stack>
                {isRichTextEnabled ? (
                    <Stack.Item
                        dir={languageDirection}
                        className="ck ck-content"
                        styles={{ root: { whiteSpace: 'break-spaces', padding: '0 5px' } }}>
                        {markdownTextToHtmlElements(displayedAnswer)}
                        {showCollapsibleControl && getCollapsibleControlComponent()}
                    </Stack.Item>
                ) : (
                    <LuisText className="ck" dir={languageDirection} shouldWrapText>
                        {displayedAnswer}
                        {showCollapsibleControl && getCollapsibleControlComponent()}
                    </LuisText>
                )}
            </Stack>
        );
    };

    const handleAddFollowUpPrompts = () => {
        if (isInEditMode) {
            increaseAnswerClicksForPair(pairId);
        }
        logButtonClick(serviceName, 'EditKb', 'FollowupPrompts', 'Qnas', currentProjectId);
        setIsPromptModalHidden(false);
    };

    const handleOnTakenTimeChange = (time: number) => {
        setTimeTakenToEditAnswers(pairId, time);
    };

    return (
        <Stack tokens={{ childrenGap: 8 }}>
            <Stack verticalFill styles={{ root: { maxWidth: '100%' } }} tokens={{ childrenGap: 16 }}>
                {isInEditMode ? (
                    isRichTextEnabled ? (
                        <Stack.Item onKeyDown={e => e.stopPropagation()}>
                            <CKEditor
                                config={{ language }}
                                data={editableAnswer}
                                editor={ClassicEditor}
                                onBlur={onSubmitAnswer}
                                onEnterKeyUp={onSubmitAnswer}
                                onChange={handleRichEditorOnChange}
                                placeholder={localize(messages.textPlaceholder)}
                            />
                        </Stack.Item>
                    ) : (
                        <LuisTextField
                            data-automation-id="answer"
                            required
                            multiline
                            autoAdjustHeight
                            resizable={false}
                            value={editableAnswer}
                            dir={languageDirection}
                            onBlur={onSubmitAnswer}
                            onEnterKeyUp={onSubmitAnswer}
                            onChange={(_, v) => handleEditAnswer(v)}
                            onTimeTakenChange={handleOnTakenTimeChange}
                            placeholder={localize(messages.textPlaceholder)}
                            styles={{ field: { resize: 'vertical !important' } }}
                        />
                    )
                ) : (
                    getReadOnlyAnswer()
                )}

                {prompts.length && (
                    <Stack tokens={{ childrenGap: 10 }}>
                        {disabled ? (
                            <Stack tokens={{ childrenGap: 5 }}>
                                {prompts.map(_prompt => (
                                    <PromptItem
                                        verticalFill
                                        key={_prompt.qnaId}
                                        verticalAlign="center"
                                        horizontalAlign="center"
                                        customProps={{ isDisabled: true }}
                                        styles={{ root: { height: '32px' } }}>
                                        <TruncatedText block nowrap maxWidth={150} dir={languageDirection} title={_prompt.displayText}>
                                            {_prompt.displayText}
                                        </TruncatedText>
                                    </PromptItem>
                                ))}
                            </Stack>
                        ) : (
                            <Stack.Item>
                                <DragAndDropList
                                    items={[...prompts]}
                                    canDragAndDrop={canWrite}
                                    onRenderItem={onRenderPromptItem}
                                    onEditItems={updatedPrompts =>
                                        onChange(
                                            answer,
                                            updatedPrompts.map((p, i) => ({ ...p, displayOrder: i }))
                                        )
                                    }
                                />
                            </Stack.Item>
                        )}
                    </Stack>
                )}
            </Stack>

            {!disabled && (
                <RBACTooltip
                    displayedUserRole={displayedUserRole}
                    hidden={canWrite || isAddActionDisabled}
                    data-automation-id="addFollowUpButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <ActionButton
                            text={localize(messages.add)}
                            onClick={handleAddFollowUpPrompts}
                            disabled={!canWrite || isAddActionDisabled}
                            iconProps={{ iconName: 'Add', style: { fontSize: '14px' } }}
                        />
                    }
                />
            )}

            {!isPromptModalHidden && (
                <QnaMakerFollowUpPromptModal
                    qnaPairsJson={qnaPairsJson}
                    hidden={isPromptModalHidden}
                    selectedQnaId={selectedQnaId}
                    isContextOnly={isContextOnly}
                    hiddenQnaPairIds={hiddenQnaPairIds}
                    onSubmit={handleFollowUpPromptSubmit}
                    prompt={prompts.find(p => p.qnaId === promptToBeEditedId)}
                    onDismiss={() => {
                        setIsPromptModalHidden(true);
                        setPromptToBeEditedId(null);
                    }}
                />
            )}
        </Stack>
    );
});
